import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowLeft, UserPlus, Mail, Trash2, Shield, AlertTriangle, Edit2, Check, X, Clock, Loader2 } from 'lucide-react'
import { useAuth, type UserRole } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import {
  useOrganizationMembersQuery,
  useTeamInvitationsQuery,
  useInviteTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useUpdateTeamMemberRoleMutation,
  type TeamMember,
} from '@/data/teamMembers'

// Roles available in the role-edit dropdown (owners cannot be demoted via the UI;
// super_admin is not assignable through team management).
type EditableRole = 'admin' | 'manager' | 'viewer'

const roleLabels: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
}

const roleBadgeColors: Record<UserRole, string> = {
  super_admin: 'bg-destructive/10 text-destructive',
  owner: 'bg-violet-500/10 text-violet-500',
  admin: 'bg-blue-500/10 text-blue-500',
  manager: 'bg-emerald-500/10 text-emerald-500',
  viewer: 'bg-muted text-muted-foreground',
}

export default function TeamManagePage() {
  const { organization, user, hasPermission } = useAuth()
  const { toast } = useToast()

  const orgId = organization?.id
  const orgName = organization?.name ?? ''
  const inviterName = user?.name ?? undefined

  const membersQuery = useOrganizationMembersQuery(orgId)
  const invitationsQuery = useTeamInvitationsQuery(orgId)
  const inviteMutation = useInviteTeamMemberMutation()
  const removeMutation = useRemoveTeamMemberMutation(orgId)
  const updateRoleMutation = useUpdateTeamMemberRoleMutation(orgId)

  const teamMembers = membersQuery.data ?? []
  const pendingInvites = invitationsQuery.data ?? []
  const isLoading = membersQuery.isLoading || invitationsQuery.isLoading

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<EditableRole>('viewer')

  // Delete confirmation
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Role editing
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null)
  const [editingRole, setEditingRole] = useState<EditableRole>('viewer')

  if (!hasPermission('manage_team')) {
    return (
      <div className="space-y-8">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-8 text-center">
          <Shield className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-destructive">Access Denied</h2>
          <p className="mt-2 text-muted-foreground">
            You don't have permission to manage team members.
          </p>
          <Button asChild className="mt-4">
            <Link to="/dashboard">Return to Dashboard</Link>
          </Button>
        </div>
      </div>
    )
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId) return

    if (!inviteEmail.includes('@')) {
      toast({
        variant: 'destructive',
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
      })
      return
    }

    try {
      await inviteMutation.mutateAsync({
        organizationId: orgId,
        email: inviteEmail,
        role: inviteRole,
        organizationName: orgName,
        inviterName,
      })
      toast({
        title: 'Invitation sent',
        description: `${inviteEmail} has been invited as ${roleLabels[inviteRole]}.`,
      })
      setInviteEmail('')
      setInviteRole('viewer')
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to send invitation',
        description: error instanceof Error ? error.message : 'An error occurred.',
      })
    }
  }

  const handleRemove = (memberId: string) => {
    setDeletingMemberId(memberId)
    setShowDeleteConfirm(true)
  }

  const confirmRemove = async () => {
    if (!deletingMemberId) return
    const member = teamMembers.find((m) => m.id === deletingMemberId)
    try {
      await removeMutation.mutateAsync(deletingMemberId)
      toast({
        title: 'Team member removed',
        description: `${member?.user.name ?? member?.user.email ?? 'Team member'} has been removed.`,
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to remove team member',
        description: error instanceof Error ? error.message : 'An error occurred.',
      })
    } finally {
      setShowDeleteConfirm(false)
      setDeletingMemberId(null)
    }
  }

  const handleStartEditRole = (member: TeamMember) => {
    setEditingMemberId(member.id)
    if (member.role === 'admin' || member.role === 'manager' || member.role === 'viewer') {
      setEditingRole(member.role)
    } else {
      setEditingRole('viewer')
    }
  }

  const handleCancelEditRole = () => {
    setEditingMemberId(null)
    setEditingRole('viewer')
  }

  const handleSaveRole = async (memberId: string) => {
    const member = teamMembers.find((m) => m.id === memberId)
    try {
      await updateRoleMutation.mutateAsync({ id: memberId, role: editingRole })
      toast({
        title: 'Role updated',
        description: `${member?.user.name ?? member?.user.email ?? 'Team member'}'s role is now ${roleLabels[editingRole]}.`,
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to update role',
        description: error instanceof Error ? error.message : 'An error occurred.',
      })
    } finally {
      setEditingMemberId(null)
      setEditingRole('viewer')
    }
  }

  const memberToDelete = teamMembers.find((m) => m.id === deletingMemberId)

  return (
    <div className="space-y-8">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && memberToDelete && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card text-card-foreground rounded-lg border shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Remove Team Member</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                </div>
              </div>
              <p className="mb-6">
                Are you sure you want to remove{' '}
                <strong>{memberToDelete.user.name ?? memberToDelete.user.email}</strong>{' '}
                ({memberToDelete.user.email}) from your team?
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={removeMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmRemove}
                  disabled={removeMutation.isPending}
                >
                  {removeMutation.isPending ? 'Removing…' : 'Remove Member'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      <Link
        to="/team"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Team
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Manage Team</h1>
          <p className="text-muted-foreground">
            Add, remove, and manage team member roles for {organization?.name}
          </p>
        </div>
        <Button asChild>
          <Link to="/team/invite">
            <UserPlus className="mr-2 h-4 w-4" />
            Invite Member
          </Link>
        </Button>
      </div>

      {/* Team Members */}
      <div className="bg-card text-card-foreground rounded-lg border">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Team Members ({teamMembers.length})</h2>
        </div>
        <div className="divide-y">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No team members yet.</div>
          ) : (
            teamMembers.map((member) => {
              const displayName = member.user.name ?? member.user.email.split('@')[0]
              const initial = displayName.charAt(0).toUpperCase()
              return (
                <div key={member.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium overflow-hidden">
                      {member.user.avatar_url ? (
                        <img src={member.user.avatar_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        initial
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{displayName}</p>
                      <p className="text-sm text-muted-foreground">{member.user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {editingMemberId === member.id ? (
                      <div className="flex items-center space-x-2">
                        <select
                          value={editingRole}
                          onChange={(e) => setEditingRole(e.target.value as EditableRole)}
                          className="px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                        </select>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-green-600 hover:bg-green-500/10"
                          onClick={() => handleSaveRole(member.id)}
                          disabled={updateRoleMutation.isPending}
                          title="Save role"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={handleCancelEditRole}
                          disabled={updateRoleMutation.isPending}
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadgeColors[member.role]}`}>
                        {roleLabels[member.role]}
                      </span>
                    )}
                    {member.role !== 'owner' && editingMemberId !== member.id && (
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartEditRole(member)}
                          title="Edit role"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <a
                          href={`mailto:${member.user.email}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted"
                          title="Email member"
                        >
                          <Mail className="h-4 w-4" />
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemove(member.id)}
                          title="Remove from team"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="bg-card text-card-foreground rounded-lg border">
          <div className="p-4 border-b">
            <h2 className="font-semibold">Pending Invitations ({pendingInvites.length})</h2>
          </div>
          <div className="divide-y">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">{invite.email}</p>
                    <p className="text-sm text-muted-foreground">
                      Invited {new Date(invite.invited_at).toLocaleDateString()} · expires{' '}
                      {new Date(invite.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadgeColors[invite.role]}`}>
                  {roleLabels[invite.role]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Invite */}
      <div className="bg-card text-card-foreground rounded-lg border p-6">
        <h3 className="font-semibold mb-4">Quick Invite</h3>
        <form onSubmit={handleInvite} className="flex gap-4">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="colleague@example.com"
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            disabled={inviteMutation.isPending}
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as EditableRole)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            disabled={inviteMutation.isPending}
          >
            <option value="viewer">Viewer</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
          <Button type="submit" disabled={inviteMutation.isPending}>
            {inviteMutation.isPending ? 'Sending…' : 'Send Invite'}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground mt-2">
          The invitee will receive an email with a link to accept and join your organization.
        </p>
      </div>
    </div>
  )
}
